/**
 * The 48 px top bar (spec M0-F4 §7.6).
 *
 * Left → right, in monday's order minus the five monday-only affordances of §3.2:
 * brand · centred search opener (475×32) · bell · inbox tray · invite · help · divider · avatar.
 * Every control is a 32×32 `IconButton` with a tooltip and an accessible name; the bar is a
 * `<header role="banner">` landmark.
 */
import { Counter, IconButton, Tooltip, Wordmark, icons } from '@elizmonday/ui';
import { Link, useNavigate } from 'react-router';
import { useShellStore } from '../modules/shell/store';
import type { ShellCapabilities } from '../modules/shell/bootstrap';
import { ProfileMenu, type ProfileMenuUser } from './ProfileMenu';

export interface TopBarProps {
  user: ProfileMenuUser | null;
  accountName: string | null;
  capabilities: ShellCapabilities;
  unread: { notifications: number; inbox: number };
  /** Rendered below 768 px, where the rail is off-canvas (§7.10). */
  onToggleOffCanvas?: () => void;
}

/**
 * E14: `maxDigits={2}` is what makes the counter render `99+`, matching the observed badge.
 *
 * P-168: each badge is addressable on its own, because the two counters answer to two different
 * things — `Mark all as read` empties the **bell's** and leaves the update feed's alone.
 */
function Badged({ count, testId, children }: { count: number; testId: string; children: React.ReactNode }) {
  if (count <= 0) return <>{children}</>;
  return (
    <span className="ez-topbar__badged">
      {children}
      <span className="ez-topbar__badge" data-testid={testId}>
        <Counter count={count} maxDigits={2} color="negative" size="small" />
      </span>
    </span>
  );
}

/**
 * P-283 (3): the counter caps at `99+`, so the tooltip carries the **exact** number — the one
 * place a person can read "6 112 unread" without opening the feed and counting.
 */
function withCount(label: string, count: number): string {
  return count > 0 ? `${label} — ${count.toLocaleString()} unread` : label;
}

export function TopBar({ user, accountName, capabilities, unread, onToggleOffCanvas }: TopBarProps) {
  const navigate = useNavigate();
  const openOverlay = useShellStore((state) => state.openOverlay);
  const toggleOverlay = useShellStore((state) => state.toggleOverlay);

  return (
    <header className="ez-topbar" role="banner" data-testid="app-topbar">
      {onToggleOffCanvas ? (
        <IconButton
          /* The phone nav toggle is a **hamburger**: `icons.menu` is Vibe 4.1's `⋯` (T-8d). */
          icon={icons.lines}
          ariaLabel="Open navigation"
          kind="tertiary"
          onClick={onToggleOffCanvas}
          data-testid="topbar-hamburger"
        />
      ) : null}

      <Link to="/" className="ez-topbar__brand" aria-label={`${accountName ?? 'Eli Z Monday'} home`}>
        <Wordmark height={24} data-testid="topbar-wordmark" />
      </Link>

      <div className="ez-topbar__search">
        <button
          type="button"
          className="ez-search-opener"
          onClick={() => openOverlay('search')}
          data-testid="topbar-search"
        >
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
            <path
              d="M9 2a7 7 0 1 1-4.2 12.6l-3.1 3.1a.75.75 0 0 1-1.06-1.06l3.1-3.1A7 7 0 0 1 9 2Zm0 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"
              fill="currentColor"
            />
          </svg>
          <span className="ez-search-opener__placeholder">Search for anything...</span>
        </button>
      </div>

      <div className="ez-topbar__spacer" />

      <div className="ez-topbar__actions">
        {capabilities.notifications ? (
          <Badged count={unread.notifications} testId="topbar-notifications-badge">
            <Tooltip content={withCount('Notifications', unread.notifications)}>
              <IconButton
                icon={icons.notifications}
                ariaLabel="Notifications"
                kind="tertiary"
                onClick={() => toggleOverlay('notifications')}
                data-testid="topbar-notifications"
              />
            </Tooltip>
          </Badged>
        ) : null}

        {capabilities.inbox ? (
          <Badged count={unread.inbox} testId="topbar-inbox-badge">
            <Tooltip content={withCount('Update feed', unread.inbox)}>
              {/* A button, not a `<Link>` wrapping one: nesting two interactive elements is
                  invalid HTML and only one of them ends up handling the click. */}
              <IconButton
                icon={icons.inbox}
                ariaLabel="Update feed"
                kind="tertiary"
                onClick={() => navigate('/my_inbox')}
                data-testid="topbar-inbox"
              />
            </Tooltip>
          </Badged>
        ) : null}

        {capabilities.inviteMembers ? (
          <Tooltip content="Invite members">
            <IconButton
              icon={icons.invite}
              ariaLabel="Invite members"
              kind="tertiary"
              onClick={() => openOverlay('invite')}
              data-testid="topbar-invite"
            />
          </Tooltip>
        ) : null}

        <Tooltip content="Keyboard shortcuts">
          <IconButton
            icon={icons.help}
            ariaLabel="Get help"
            kind="tertiary"
            onClick={() => openOverlay('shortcuts')}
            data-testid="topbar-help"
          />
        </Tooltip>

        <span className="ez-topbar__divider" aria-hidden="true" />

        <ProfileMenu user={user} accountName={accountName} capabilities={capabilities} />
      </div>
    </header>
  );
}
